import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

interface Concept {
  id: string;
  title: string;
  description: string;
  category: string;
  relatedPapers: string[];
  connections: string[];
  x?: number;
  y?: number;
}

const concepts: Concept[] = [
  {
    id: 'llm-evaluation',
    title: 'LLM Evaluation',
    description: 'Systematic assessment of Large Language Model capabilities, including reasoning, factuality, and domain-specific performance.',
    category: 'AI Research',
    relatedPapers: ['SMLB', 'DeepSeek R1 Analysis'],
    connections: ['medical-ai', 'chain-of-thought', 'benchmarking']
  },
  {
    id: 'medical-ai',
    title: 'Medical AI',
    description: 'Application of artificial intelligence in healthcare for diagnosis, treatment planning, and clinical decision support.',
    category: 'Healthcare',
    relatedPapers: ['Harm Reduction Strategies', 'SMLB'],
    connections: ['llm-evaluation', 'harm-reduction', 'speech-biomarkers']
  },
  {
    id: 'chain-of-thought',
    title: 'Chain-of-Thought',
    description: 'Reasoning paradigm where LLMs generate intermediate steps before final answers, improving complex problem-solving.',
    category: 'AI Research',
    relatedPapers: ['DeepSeek R1 Analysis'],
    connections: ['llm-evaluation', 'reasoning-failures']
  },
  {
    id: 'speech-biomarkers',
    title: 'Speech Biomarkers',
    description: 'Acoustic and linguistic features in speech that indicate neurological or cognitive conditions.',
    category: 'Clinical Research',
    relatedPapers: ['Dementia Detection', 'Voice Cloning'],
    connections: ['medical-ai', 'dementia-detection', 'dysarthria']
  },
  {
    id: 'harm-reduction',
    title: 'Harm Reduction',
    description: 'Strategies to minimize potential negative impacts of AI systems while maximizing benefits.',
    category: 'AI Safety',
    relatedPapers: ['Harm Reduction Strategies'],
    connections: ['medical-ai', 'llm-evaluation']
  },
  {
    id: 'dementia-detection',
    title: 'Early Dementia Detection',
    description: 'Using multimodal AI to identify early signs of cognitive decline through speech, behavior, and interaction patterns.',
    category: 'Clinical Research',
    relatedPapers: ['Multimodal Dementia Detection'],
    connections: ['speech-biomarkers', 'medical-ai']
  },
  {
    id: 'dysarthria',
    title: 'Dysarthric Speech',
    description: 'Motor speech disorders affecting articulation, used in research for ALS and cerebral palsy patients.',
    category: 'Speech Technology',
    relatedPapers: ['Voice Cloning for Dysarthria'],
    connections: ['speech-biomarkers', 'synthetic-speech']
  },
  {
    id: 'synthetic-speech',
    title: 'Synthetic Speech',
    description: 'AI-generated speech for data augmentation, voice preservation, and assistive technology.',
    category: 'Speech Technology',
    relatedPapers: ['Voice Cloning', 'CVPR Synthetic Data'],
    connections: ['dysarthria', 'speech-biomarkers']
  },
  {
    id: 'reasoning-failures',
    title: 'Reasoning Failures',
    description: 'Systematic analysis of where and why LLMs fail in logical and mathematical reasoning tasks.',
    category: 'AI Research',
    relatedPapers: ['Mathematical Reasoning Failures'],
    connections: ['chain-of-thought', 'llm-evaluation']
  },
  {
    id: 'benchmarking',
    title: 'AI Benchmarking',
    description: 'Creating standardized tests to measure AI system performance across different tasks and domains.',
    category: 'AI Research',
    relatedPapers: ['SMLB', 'Swedish LLM Benchmark'],
    connections: ['llm-evaluation', 'medical-ai']
  }
];

const colors = {
  deep: '#0D0A0F',
  accent1: '#FCCB06',
  accent2: '#E5A83B',
  highlight: '#FFEBA3',
  text: '#FFFCF0',
};

const categoryColors: Record<string, string> = {
  'AI Research': '#FCCB06',
  'Healthcare': '#4ADE80',
  'Clinical Research': '#60A5FA',
  'AI Safety': '#F472B6',
  'Speech Technology': '#A78BFA'
};

export default function ConceptsExplorer() {
  const [selectedConcept, setSelectedConcept] = useState<Concept | null>(null);
  const [hoveredConcept, setHoveredConcept] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [positions, setPositions] = useState<Record<string, { x: number; y: number }>>({});

  // Initialize positions in a circular layout
  useEffect(() => {
    const centerX = 400;
    const centerY = 300;
    const radius = 200;
    const newPositions: Record<string, { x: number; y: number }> = {};

    concepts.forEach((concept, index) => {
      const angle = (index / concepts.length) * 2 * Math.PI - Math.PI / 2;
      newPositions[concept.id] = {
        x: centerX + radius * Math.cos(angle),
        y: centerY + radius * Math.sin(angle)
      };
    });

    setPositions(newPositions);
    setLoaded(true);
  }, []);

  // Draw connections
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || Object.keys(positions).length === 0) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw connections
    concepts.forEach(concept => {
      const start = positions[concept.id];
      if (!start) return;

      concept.connections.forEach(targetId => {
        const end = positions[targetId];
        if (!end) return;

        const isHighlighted =
          hoveredConcept === concept.id ||
          hoveredConcept === targetId ||
          selectedConcept?.id === concept.id ||
          selectedConcept?.id === targetId;

        ctx.beginPath();
        ctx.moveTo(start.x, start.y);
        ctx.lineTo(end.x, end.y);
        ctx.strokeStyle = isHighlighted
          ? `${colors.accent1}80`
          : `${colors.accent1}20`;
        ctx.lineWidth = isHighlighted ? 2 : 1;
        ctx.stroke();
      });
    });
  }, [positions, hoveredConcept, selectedConcept]);

  return (
    <div
      className="min-h-screen text-white"
      style={{
        background: colors.deep,
        opacity: loaded ? 1 : 0,
        transition: 'opacity 1s ease-in-out'
      }}
    >
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[100vw] h-[50vh] rounded-full opacity-10"
          style={{
            background: `radial-gradient(ellipse, ${colors.accent1}30, transparent 70%)`,
            filter: 'blur(100px)'
          }}
        />
      </div>

      {/* Header */}
      <header className="relative z-10 px-8 py-12">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <Link
            to="/"
            className="text-white/60 hover:text-amber-200 transition-colors text-sm tracking-widest"
          >
            &larr; BACK TO HOME
          </Link>
          <h1 className="text-2xl font-thin tracking-wider">
            CONCEPTS <span style={{ color: colors.highlight }}>EXPLORER</span>
          </h1>
        </div>
      </header>

      {/* Main Content */}
      <div className="relative z-10 px-8 py-12">
        <div className="max-w-7xl mx-auto">
          {/* Hero */}
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-thin mb-6">
              Research <span style={{ color: colors.highlight }}>Concepts</span>
            </h2>
            <p className="text-xl text-white/60 font-extralight max-w-2xl mx-auto">
              Explore the interconnected concepts that form the foundation of my research in AI, healthcare, and cognitive science.
            </p>
          </div>

          {/* Concept Network Visualization */}
          <div className="relative mb-16">
            <div
              className="relative mx-auto rounded-3xl overflow-hidden"
              style={{
                width: '800px',
                maxWidth: '100%',
                height: '600px',
                background: 'rgba(13, 10, 15, 0.8)',
                border: `1px solid ${colors.accent1}20`
              }}
            >
              <canvas
                ref={canvasRef}
                width={800}
                height={600}
                className="absolute inset-0"
              />

              {/* Concept Nodes */}
              {concepts.map(concept => {
                const pos = positions[concept.id];
                if (!pos) return null;

                const isSelected = selectedConcept?.id === concept.id;
                const isHovered = hoveredConcept === concept.id;
                const isConnected = selectedConcept?.connections.includes(concept.id) ||
                  (hoveredConcept && concepts.find(c => c.id === hoveredConcept)?.connections.includes(concept.id));

                return (
                  <div
                    key={concept.id}
                    className="absolute cursor-pointer transform -translate-x-1/2 -translate-y-1/2 transition-all duration-300"
                    style={{
                      left: pos.x,
                      top: pos.y,
                      zIndex: isSelected || isHovered ? 20 : 10
                    }}
                    onClick={() => setSelectedConcept(isSelected ? null : concept)}
                    onMouseEnter={() => setHoveredConcept(concept.id)}
                    onMouseLeave={() => setHoveredConcept(null)}
                  >
                    {/* Node */}
                    <div
                      className="relative flex items-center justify-center transition-all duration-300"
                      style={{
                        width: isSelected || isHovered ? '100px' : '80px',
                        height: isSelected || isHovered ? '100px' : '80px',
                        borderRadius: '50%',
                        background: `radial-gradient(circle, ${categoryColors[concept.category]}30, ${categoryColors[concept.category]}10)`,
                        border: `2px solid ${isSelected || isHovered ? categoryColors[concept.category] : categoryColors[concept.category] + '50'}`,
                        boxShadow: isSelected || isHovered
                          ? `0 0 30px ${categoryColors[concept.category]}50`
                          : 'none',
                        opacity: (isConnected || isSelected || isHovered || (!selectedConcept && !hoveredConcept)) ? 1 : 0.3
                      }}
                    >
                      <span
                        className="text-xs text-center px-2 font-medium"
                        style={{
                          color: isSelected || isHovered ? colors.text : colors.text + 'AA',
                          textShadow: isSelected || isHovered ? `0 0 10px ${categoryColors[concept.category]}` : 'none'
                        }}
                      >
                        {concept.title}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Legend */}
            <div className="flex flex-wrap justify-center gap-6 mt-8">
              {Object.entries(categoryColors).map(([category, color]) => (
                <div key={category} className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ background: color }}
                  />
                  <span className="text-sm text-white/60">{category}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Selected Concept Detail */}
          {selectedConcept && (
            <div
              className="max-w-2xl mx-auto p-8 rounded-2xl mb-16 transition-all duration-500"
              style={{
                background: 'rgba(13, 10, 15, 0.8)',
                border: `1px solid ${categoryColors[selectedConcept.category]}50`,
                boxShadow: `0 0 50px ${categoryColors[selectedConcept.category]}20`
              }}
            >
              <div className="flex items-start justify-between mb-4">
                <div
                  className="px-3 py-1 rounded-full text-xs"
                  style={{
                    background: `${categoryColors[selectedConcept.category]}20`,
                    color: categoryColors[selectedConcept.category]
                  }}
                >
                  {selectedConcept.category}
                </div>
                <button
                  onClick={() => setSelectedConcept(null)}
                  className="text-white/40 hover:text-white transition-colors"
                >
                  &times;
                </button>
              </div>

              <h3
                className="text-3xl font-thin mb-4"
                style={{ color: categoryColors[selectedConcept.category] }}
              >
                {selectedConcept.title}
              </h3>

              <p className="text-white/70 text-lg leading-relaxed mb-6 font-extralight">
                {selectedConcept.description}
              </p>

              <div className="mb-4">
                <h4 className="text-sm text-white/40 tracking-wider mb-2">RELATED PAPERS</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedConcept.relatedPapers.map(paper => (
                    <span
                      key={paper}
                      className="px-3 py-1 rounded-full text-sm"
                      style={{ background: 'rgba(255,255,255,0.05)', color: colors.accent1 }}
                    >
                      {paper}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="text-sm text-white/40 tracking-wider mb-2">CONNECTED CONCEPTS</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedConcept.connections.map(connId => {
                    const conn = concepts.find(c => c.id === connId);
                    return conn ? (
                      <button
                        key={connId}
                        onClick={() => setSelectedConcept(conn)}
                        className="px-3 py-1 rounded-full text-sm transition-colors hover:opacity-80"
                        style={{
                          background: `${categoryColors[conn.category]}20`,
                          color: categoryColors[conn.category]
                        }}
                      >
                        {conn.title}
                      </button>
                    ) : null;
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Concept Cards Grid */}
          <div className="mt-16">
            <h3 className="text-2xl font-thin text-center mb-12">
              All <span style={{ color: colors.highlight }}>Concepts</span>
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {concepts.map(concept => (
                <button
                  key={concept.id}
                  onClick={() => setSelectedConcept(concept)}
                  className="text-left p-6 rounded-xl transition-all duration-300 hover:-translate-y-1"
                  style={{
                    background: selectedConcept?.id === concept.id
                      ? `linear-gradient(135deg, ${categoryColors[concept.category]}20, ${colors.deep})`
                      : 'rgba(13, 10, 15, 0.6)',
                    border: `1px solid ${selectedConcept?.id === concept.id ? categoryColors[concept.category] : 'rgba(255,255,255,0.1)'}`
                  }}
                >
                  <div
                    className="inline-block px-2 py-1 rounded text-xs mb-3"
                    style={{
                      background: `${categoryColors[concept.category]}20`,
                      color: categoryColors[concept.category]
                    }}
                  >
                    {concept.category}
                  </div>
                  <h4 className="text-lg font-thin mb-2 text-white">{concept.title}</h4>
                  <p className="text-white/50 text-sm font-extralight line-clamp-2">
                    {concept.description}
                  </p>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="relative z-10 py-20 text-center">
        <div className="flex justify-center gap-4">
          <Link
            to="/research"
            className="inline-block px-8 py-3 rounded-full text-sm tracking-wider transition-all hover:scale-105"
            style={{
              background: `linear-gradient(to right, ${colors.accent1}40, ${colors.accent2}30)`,
              border: `1px solid ${colors.accent1}50`
            }}
          >
            EXPLORE PUBLICATIONS
          </Link>
          <Link
            to="/"
            className="inline-block px-8 py-3 rounded-full text-sm tracking-wider transition-all hover:scale-105"
            style={{
              background: 'transparent',
              border: `1px solid ${colors.accent1}30`
            }}
          >
            BACK TO MAIN
          </Link>
        </div>
      </footer>

      <style>{`
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
}
